import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionPage from './CollectionPage';
import { useCollectionsStore } from '../../store/collectionsStore';
import { useVariablesStore } from '../../store/variablesStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { getFolderById } from '../../utils/collectionFolders';
import { ancestorFolderVariables } from '../../utils/folderVariables';
import { stripVariableRowIds } from '../../utils/collectionTabState';
import { withFolderPageFields } from '../../utils/folderPageFields';
import { isSyncEngineWorkspace } from '../../utils/workspaceStorage';
import { OUTBOX_ITEM_MAX_BYTES, recordBytes } from '../../mcp/collectionsDb';
import { isFolderReadOnly } from '../../hooks/useFolderSave';
import { useFolderPageSave } from '../../hooks/useFolderPageSave';

const NOTICE_CLASS = 'flex-shrink-0 px-5 py-2 text-xs border-b border-gray-200 dark:border-zinc-700';

/**
 * The persisted collection RECORD in a sync-engine workspace is the collection
 * minus `requests` (src-tauri sync/store.rs), and the engine drops an op over
 * the cap without an error. D8: warn, do not refuse.
 */
function recordBytesWith(collection, folderId, tab) {
  const next = withFolderPageFields(collection, folderId, {
    documentation: tab.documentation,
    auth: tab.auth,
    variables: stripVariableRowIds(tab.variables),
    preRequestScript: tab.preRequestScript,
    testScript: tab.testScript,
  });
  const { requests: _requests, ...record } = next;
  return recordBytes(record);
}

/**
 * The folder page (owner D1–D3, 28.09.2026): the collection page's shell and
 * sections over ONE folder of `collection.settings.folders[]`.
 *
 * - Read-only by the same gate the save enforces (viewer collection, read-only
 *   team workspace).
 * - `{{chips}}` in its values resolve against the collection's variables and
 *   the ANCESTOR folders (a folder's own rows are what is being edited).
 * - A folder deleted elsewhere shows as gone instead of editing thin air.
 */
export default function FolderEditor({ initialData, ...rest }) {
  const { t } = useTranslation();
  const collections = useCollectionsStore((s) => s.collections);
  const collectionVariablesById = useVariablesStore((s) => s.collectionVariablesById);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const saveFolderPage = useFolderPageSave();

  const collectionId = initialData?.collectionId;
  const folderId = initialData?.folderId;
  const collection = useMemo(
    () => (collections || []).find((c) => String(c.id) === String(collectionId)) ?? null,
    [collections, collectionId]
  );
  const folder = collection ? getFolderById(collection, folderId) : null;
  const canEdit = !!folder && !isFolderReadOnly(collection, currentWorkspace, isAuthenticated);

  const extraInputProps = useMemo(
    () => ({
      collectionVariables: collectionVariablesById?.[collectionId]
        || collection?.settings?.collection_variables || [],
      folderVariables: collection ? ancestorFolderVariables(collection, folderId) : [],
    }),
    [collectionVariablesById, collectionId, collection, folderId]
  );

  const oversize = useMemo(() => {
    if (!canEdit || !isSyncEngineWorkspace(currentWorkspace, isAuthenticated)) return null;
    const bytes = recordBytesWith(collection, folderId, initialData);
    return bytes > OUTBOX_ITEM_MAX_BYTES ? bytes : null;
  }, [canEdit, currentWorkspace, isAuthenticated, collection, folderId, initialData]);

  if (!folder) {
    return (
      <div className="editor-empty-surface flex h-full min-h-[120px] items-center justify-center px-4 text-sm text-gray-500" role="note">
        {t('collectionPage.folderGone', { name: initialData?.name || '' })}
      </div>
    );
  }

  const notice = (
    <>
      {!canEdit && (
        <p className={`${NOTICE_CLASS} text-gray-600 dark:text-zinc-300`} role="note">{t('collectionPage.folderReadOnly')}</p>
      )}
      {oversize != null && (
        <p className={`${NOTICE_CLASS} text-amber-600`} role="alert">
          {t('collectionPage.sizeWarning', {
            size: Math.ceil(oversize / 1000),
            cap: Math.floor(OUTBOX_ITEM_MAX_BYTES / 1000),
          })}
        </p>
      )}
    </>
  );

  return (
    <CollectionPage
      {...rest}
      kind="folder"
      initialData={initialData}
      canEdit={canEdit}
      onSave={saveFolderPage}
      extraInputProps={extraInputProps}
      notice={notice}
    />
  );
}
